import { createFileRoute } from '@tanstack/react-router'
import { StaticNav } from '@/components/static-nav'
import { StaticFooter } from '@/components/static-footer'
import { SignInButton } from '@/components/sign-in-button'
import {
  IconInbox,
  IconSparkles,
  IconTrash,
  IconArrowForward,
  IconMessage,
  IconClock,
  IconCircleCheck,
  IconBrain,
  IconMailOff,
  IconFilter,
  IconCheck,
  IconBolt
} from '@tabler/icons-react'

export const Route = createFileRoute('/inbox-zero')({
  component: InboxZeroPage,
})

function InboxZeroPage() {
  const actions = [
    { icon: <IconTrash className="w-5 h-5" />, action: "Delete/Archive", desc: "If you don't need it, remove it", color: "red" },
    { icon: <IconArrowForward className="w-5 h-5" />, action: "Delegate", desc: "Forward to someone better suited", color: "orange" },
    { icon: <IconMessage className="w-5 h-5" />, action: "Respond", desc: "If it takes < 2 minutes, do it now", color: "blue" },
    { icon: <IconClock className="w-5 h-5" />, action: "Defer", desc: "Add to your task list for later", color: "purple" },
    { icon: <IconCircleCheck className="w-5 h-5" />, action: "Do", desc: "If you have time, handle it now", color: "green" },
  ]

  const steps = [
    {
      step: "1",
      title: "The Initial Purge",
      description: "If you have thousands of emails, don't try to process them individually. Archive everything older than 30 days and start fresh. You can always search for old emails if needed."
    },
    {
      step: "2",
      title: "Process in Batches",
      description: "Instead of checking email constantly, process it in scheduled batches—perhaps three times a day. During each session, process every email to zero."
    },
    {
      step: "3",
      title: "Use the 2-Minute Rule",
      description: "If an email can be handled in 2 minutes or less, do it immediately. This prevents small tasks from piling up and cluttering your mind."
    },
    {
      step: "4",
      title: "Unsubscribe Ruthlessly",
      description: "Every newsletter you don't read is future clutter. Unsubscribe from anything that doesn't provide consistent value."
    }
  ]

  const maintenanceTips = [
    { icon: <IconClock className="w-5 h-5" />, tip: "Check email at set times, not constantly" },
    { icon: <IconInbox className="w-5 h-5" />, tip: "Process to zero during each email session" },
    { icon: <IconFilter className="w-5 h-5" />, tip: "Use filters to auto-archive low-priority emails" },
    { icon: <IconMailOff className="w-5 h-5" />, tip: "Unsubscribe immediately from unwanted emails" },
    { icon: <IconMessage className="w-5 h-5" />, tip: "Keep your email responses short and actionable" },
  ]

  return (
    <div className="bg-[#fafafa] text-slate-950 min-h-screen">
      <StaticNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-24">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-blue-100/40 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-blue-50 border border-blue-100">
              <IconInbox className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">
                The complete guide
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Inbox Zero:{' '}
              <span className="text-blue-600">Empty Inbox, Clear Mind</span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Inbox Zero isn't about having zero emails—it's about spending
              minimal time and mental energy on email by processing it efficiently.
            </p>
          </div>

          {/* Quote Box */}
          <div className="bg-blue-50 border border-blue-100 rounded-3xl p-8 text-center max-w-2xl mx-auto">
            <IconBrain className="w-10 h-10 text-blue-600 mx-auto mb-4" />
            <p className="text-lg text-slate-700 italic mb-2">
              "The 'zero' refers to the amount of time your brain spends
              thinking about email, not just the number of messages in your inbox."
            </p>
            <p className="text-sm text-slate-500">— Merlin Mann, creator of Inbox Zero</p>
          </div>
        </div>
      </section>

      {/* The 5 Actions */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            The 5 Actions of Inbox Zero
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            For every email, take one of these five actions
          </p>

          <div className="grid md:grid-cols-5 gap-4">
            {actions.map((item, i) => {
              const colorClasses = {
                red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'bg-red-100 text-red-600' },
                orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-100 text-orange-600' },
                blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-100 text-blue-600' },
                purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-100 text-purple-600' },
                green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'bg-green-100 text-green-600' },
              }[item.color] || { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'bg-slate-100 text-slate-600' }

              return (
                <div key={i} className={`${colorClasses.bg} ${colorClasses.border} border rounded-3xl p-5 text-center`}>
                  <div className={`w-12 h-12 rounded-lg ${colorClasses.icon} flex items-center justify-center mx-auto mb-3`}>
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm mb-1">{item.action}</h3>
                  <p className="text-xs text-slate-500">{item.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            How to Achieve Inbox Zero
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Four steps to get your inbox under control
          </p>

          <div className="space-y-6">
            {steps.map((step, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold">
                  {step.step}
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Maintenance Tips */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Maintaining Inbox Zero
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Keep your inbox at zero with these habits
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {maintenanceTips.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                  {item.icon}
                </div>
                <p className="text-sm text-slate-700 pt-2">{item.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Tools */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-white/20 text-sm font-medium">
                  <IconSparkles className="w-4 h-4" />
                  AI-Powered
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  Tools for Inbox Zero
                </h2>
                <p className="text-blue-100 mb-6">
                  While Inbox Zero can be achieved manually, AI-powered tools like
                  Winbox Zero make it significantly easier by automatically categorizing
                  emails into Inbox, Delete, Archive, and Unsubscribe.
                </p>
                <ul className="space-y-3">
                  {[
                    "Analyze up to 5,000 emails at once",
                    "AI categorizes in minutes, not hours",
                    "Visual Kanban board for processing",
                    "Track progress toward inbox zero"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <IconCheck className="w-5 h-5 text-green-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="bg-white rounded-3xl p-6 shadow-2xl">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                      <IconCheck className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="text-3xl font-bold text-slate-900">0</div>
                    <div className="text-sm text-slate-500">emails in inbox</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-center text-xs">
                    <div className="bg-slate-50 rounded-lg p-2">
                      <div className="font-bold text-slate-900">1,234</div>
                      <div className="text-slate-500">archived</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2">
                      <div className="font-bold text-slate-900">567</div>
                      <div className="text-slate-500">deleted</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-slate-200 bg-[#fafafa] px-6 py-32">
        <div className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 px-8 py-20 shadow-2xl sm:px-16 sm:py-24 text-center">
            <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl mix-blend-screen" />
            <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-purple-500/20 blur-3xl mix-blend-screen" />
            
            <div className="relative z-10">
              <h2 className="mx-auto max-w-2xl text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                Achieve inbox zero today
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-slate-300">
                Get started today with unlimited email processing.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4">
                <SignInButton variant="dark" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <StaticFooter />
    </div>
  )
}
